'use client';

// Inspired by react-hot-toast library
import * as React from 'react';

import type { ToastActionElement, ToastProps } from '@/components/ui/toast';
import { getErrorMessage } from '@/lib/error-message';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;
// Long enough to read both the cause and the next step
const ERROR_TOAST_DURATION_MS = 5000;
// Matches the exit animation (data-[state=closed]:duration-100 in toast.tsx):
// the toast on screen slides out before the next one slides in.
const TOAST_SWAP_DELAY_MS = 100;

export const INTERNAL_ERROR_MESSAGE =
  'Something went wrong, please try again later.';
export const INTERNAL_ERROR_TOAST: ToasterToast = {
  id: 'internal-error',
  title: 'Error',
  description: INTERNAL_ERROR_MESSAGE,
  variant: 'destructive',
  duration: ERROR_TOAST_DURATION_MS,
};

export const UNSAVED_CHANGES_TOAST: ToasterToast = {
  id: 'unsaved-changes',
  title: 'Unsaved Changes',
  description:
    'Something went wrong and there are unsaved changes, please refresh and contact support if the problem persists.',
  variant: 'destructive',
  duration: Infinity,
};

type ToasterUserProps = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

type ToasterToast = ToastProps & ToasterUserProps;

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType['ADD_TOAST'];
      toast: ToasterToast;
    }
  | {
      type: ActionType['UPDATE_TOAST'];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType['DISMISS_TOAST'];
      toastId?: ToasterToast['id'];
    }
  | {
      type: ActionType['REMOVE_TOAST'];
      toastId?: ToasterToast['id'];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, 'id'>;

// The toast waiting for the one on screen to finish sliding out.
let pendingToast:
  | { id: string; timeout: ReturnType<typeof setTimeout> }
  | undefined;

function toast({ ...props }: Toast) {
  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id },
    });
  const dismiss = () => {
    if (pendingToast?.id === id) {
      clearTimeout(pendingToast.timeout);
      pendingToast = undefined;
      return;
    }
    dispatch({ type: 'DISMISS_TOAST', toastId: id });
  };
  const show = () =>
    dispatch({
      type: 'ADD_TOAST',
      toast: {
        ...props,
        id,
        open: true,
        onOpenChange: (open) => {
          if (!open) dismiss();
        },
      },
    });

  // One toast at a time (TOAST_LIMIT): close the current one, then open this one.
  // A toast still waiting its turn is superseded by this newer one.
  const mustReplace =
    pendingToast !== undefined || memoryState.toasts.some((t) => t.open);
  if (mustReplace) {
    clearTimeout(pendingToast?.timeout);
    dispatch({ type: 'DISMISS_TOAST' });
    pendingToast = {
      id,
      timeout: setTimeout(() => {
        pendingToast = undefined;
        show();
      }, TOAST_SWAP_DELAY_MS),
    };
  } else {
    show();
  }

  return {
    id: id,
    dismiss,
    update,
  };
}

const showErrorToast = (error: unknown) =>
  toast({
    ...getErrorMessage(error),
    variant: 'destructive',
    duration: ERROR_TOAST_DURATION_MS,
  });

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  };
}

export { useToast, toast, showErrorToast };
